import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  CircularProgress,
  Fab,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { type Building, buildingLutValueList } from 'services'
import { BuildingCard } from 'components/us/building/building-card'
import { useGlobalMessageHandler, useBuildingLuts, useExecOrgParentLuts } from 'components'
import { useSessionContext } from 'session'
import {
  BUILDING_CHAMBER_OPTIONS,
  buildingChamberMode,
  BUILDING_LEG_TYPE_OPTIONS,
  federalExecChamberOptions,
  getEmptyBuildingAddress
} from 'helpers'

export const BuildingsPage: React.FC = () => {
  const [buildings, setBuildings] = useState<Building[]>([])
  const [loading, setLoading] = useState(true)
  const [showNewCard, setShowNewCard] = useState(false)
  const [filterLegType, setFilterLegType] = useState('')
  const [filterChamber, setFilterChamber] = useState('')
  const [filterState, setFilterState] = useState('')
  const { changeModuleName } = useSessionContext()
  const { showErrorMessage } = useGlobalMessageHandler()
  const { lutValues: buildingLutValues, setLutValues: setBuildingLutValues } = useBuildingLuts()
  const { lutValues: parentLut } = useExecOrgParentLuts()

  useEffect(() => {
    changeModuleName('Manage Buildings')
    void fetchBuildings()
  }, [])

  const fetchBuildings = async (): Promise<void> => {
    setLoading(true)
    try {
      const data = await buildingLutValueList()
      setBuildings(data.buildings)
      setBuildingLutValues(data)
    } catch {
      showErrorMessage('Failed to load buildings. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSaved = (saved: Building): void => {
    const updated = buildings.some(b => b.id === saved.id)
      ? buildings.map(b => b.id === saved.id ? saved : b)
      : [...buildings, saved]
    setBuildings(updated)
    setBuildingLutValues({ ...buildingLutValues, buildings: updated })
  }

  const handleDeleted = (id: number): void => {
    const updated = buildings.filter(b => b.id !== id)
    setBuildings(updated)
    setBuildingLutValues({ ...buildingLutValues, buildings: updated })
  }

  const stateOptions = useMemo(() => {
    const states = buildings
      .map(b => b.state)
      .filter((s): s is string => s != null && s !== '')
    return Array.from(new Set(states)).sort((a, b) => a.localeCompare(b))
  }, [buildings])

  const chamberFilterMode = buildingChamberMode(filterLegType)
  const chamberFilterOptions = chamberFilterMode === 'federalExec'
    ? federalExecChamberOptions(parentLut.parents)
    : BUILDING_CHAMBER_OPTIONS.filter(o => o.value !== '')

  const handleLegTypeFilter = (e: SelectChangeEvent): void => {
    setFilterLegType(e.target.value)
    setFilterChamber('')
  }

  const filtered = useMemo(() => buildings.filter(b => {
    if (filterLegType !== '' && b.legType !== filterLegType) return false
    if (filterChamber !== '' && b.chamber !== filterChamber) return false
    if (filterState !== '' && b.state !== filterState) return false
    return true
  }), [buildings, filterLegType, filterChamber, filterState])

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Buildings</Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={3}>
          <FormControl size="small" fullWidth>
            <InputLabel>Leg Type</InputLabel>
            <Select
              label="Leg Type"
              value={filterLegType}
              onChange={handleLegTypeFilter}
            >
              <MenuItem value="">All</MenuItem>
              {BUILDING_LEG_TYPE_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {chamberFilterMode !== 'none' && (
          <Grid item xs={12} sm={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>{chamberFilterMode === 'federalExec' ? 'Organization' : 'Chamber'}</InputLabel>
              <Select
                label={chamberFilterMode === 'federalExec' ? 'Organization' : 'Chamber'}
                value={filterChamber}
                onChange={(e: SelectChangeEvent) => { setFilterChamber(e.target.value) }}
              >
                <MenuItem value="">All</MenuItem>
                {chamberFilterOptions.map(o => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
        <Grid item xs={12} sm={3}>
          <FormControl size="small" fullWidth>
            <InputLabel>State</InputLabel>
            <Select
              label="State"
              value={filterState}
              onChange={(e: SelectChangeEvent) => { setFilterState(e.target.value) }}
            >
              <MenuItem value="">All</MenuItem>
              {stateOptions.map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {filtered.length} of {buildings.length} buildings
          </Typography>
        </Grid>
      </Grid>

      {showNewCard && (
        <BuildingCard
          building={getEmptyBuildingAddress()}
          isNew
          onSaved={saved => { setShowNewCard(false); handleSaved(saved) }}
          onDeleted={() => {}}
          onCancelNew={() => { setShowNewCard(false) }}
        />
      )}

      {loading
        ? <CircularProgress />
        : filtered.map(building => (
          <BuildingCard
            key={building.id}
            building={building}
            onSaved={handleSaved}
            onDeleted={handleDeleted}
          />
        ))
      }

      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 32, right: 32 }}
        onClick={() => { setShowNewCard(true) }}
        disabled={showNewCard}
      >
        <AddIcon />
      </Fab>
    </Box>
  )
}
